/**
 * RBAC v1.1 — project-scoped roles + pass lifecycle state.
 * Platform hats: Console uses operator/superadmin; design-lab uses developer (separate allowlist later).
 * Policy is enforced in application code; Postgres RLS remains the tenant isolation wall.
 *
 * Support sessions: docs/auth/operator-support-sessions.md — capability envelope, not owner impersonation.
 */

export const PROJECT_MEMBER_ROLES = ['owner', 'editor'] as const;
export type ProjectMemberRole = (typeof PROJECT_MEMBER_ROLES)[number];

/** Console allowlist roles (`platform_operators.role`). */
export const PLATFORM_OPERATOR_ROLES = ['operator', 'superadmin'] as const;
export type PlatformOperatorRole = (typeof PLATFORM_OPERATOR_ROLES)[number];

/** All platform hats (Console + future Design Lab). */
export const PLATFORM_ROLES = ['operator', 'superadmin', 'developer'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export const STUDIO_PASS_KINDS = ['preview', 'claimed'] as const;
export type StudioPassKind = (typeof STUDIO_PASS_KINDS)[number];

export const AUTHZ_ACTIONS = [
  'project.read',
  'draft.edit',
  'project.reconfigure',
  'project.delete',
  'project.pause',
  'publish.staging',
  'publish.production',
  'publish.domains',
  'admin.read',
  'operators.manage',
] as const;

export type AuthzAction = (typeof AUTHZ_ACTIONS)[number];

/** Master Console support presets (capability envelopes). Delete never included. */
export const SUPPORT_SESSION_PRESET_IDS = ['edit', 'maintain', 'full'] as const;
export type SupportSessionPresetId = (typeof SUPPORT_SESSION_PRESET_IDS)[number];

export const SUPPORT_SESSION_PRESETS: Record<
  SupportSessionPresetId,
  {
    id: SupportSessionPresetId;
    label: string;
    description: string;
    capabilities: readonly AuthzAction[];
  }
> = {
  edit: {
    id: 'edit',
    label: 'Edit',
    description: 'Draft content, media, theme, branding — no Operate / pause / publish.',
    capabilities: ['project.read', 'draft.edit'],
  },
  maintain: {
    id: 'maintain',
    label: 'Maintain',
    description: 'Edit + Operate + reconfigure + pause — no staging / promote / domains.',
    capabilities: ['project.read', 'draft.edit', 'project.reconfigure', 'project.pause'],
  },
  full: {
    id: 'full',
    label: 'Full',
    description: 'Maintain + staging, promote, and domains. Delete still blocked.',
    capabilities: [
      'project.read',
      'draft.edit',
      'project.reconfigure',
      'project.pause',
      'publish.staging',
      'publish.production',
      'publish.domains',
    ],
  },
};

/** Default preset when omitted = Full (minus delete). */
export const SUPPORT_SESSION_DEFAULT_PRESET: SupportSessionPresetId = 'full';

/** Union of all preset capabilities (max grantable via presets). */
export const SUPPORT_SESSION_DEFAULT_CAPABILITIES =
  SUPPORT_SESSION_PRESETS.full.capabilities;

export const SUPPORT_SESSION_DEFAULT_TTL_MINUTES = 120;
export const SUPPORT_SESSION_MAX_TTL_MINUTES = 360;
export const SUPPORT_SESSION_MIN_TTL_MINUTES = 15;
/** One-time redeem ticket lifetime (ms). Independent of session TTL. */
export const SUPPORT_TICKET_TTL_MS = 90_000;

export function isSupportSessionPresetId(value: string): value is SupportSessionPresetId {
  return (SUPPORT_SESSION_PRESET_IDS as readonly string[]).includes(value);
}

export function resolveSupportSessionPreset(
  presetId?: string | null,
): (typeof SUPPORT_SESSION_PRESETS)[SupportSessionPresetId] {
  if (presetId && isSupportSessionPresetId(presetId)) {
    return SUPPORT_SESSION_PRESETS[presetId];
  }
  return SUPPORT_SESSION_PRESETS[SUPPORT_SESSION_DEFAULT_PRESET];
}

/** UI helpers from a support capability list. */
export function supportCapabilityFlags(capabilities: readonly AuthzAction[] | null | undefined) {
  const set = new Set(capabilities ?? []);
  const canPublishPath =
    set.has('publish.staging') || set.has('publish.production') || set.has('publish.domains');
  return {
    canRead: set.has('project.read'),
    canDraftEdit: set.has('draft.edit'),
    canReconfigure: set.has('project.reconfigure'),
    canPause: set.has('project.pause'),
    canPublishPath,
    canDelete: set.has('project.delete'),
    /** Operate is part of Maintain/Full presets, not Edit-only. */
    showOperate: set.has('project.reconfigure') || set.has('project.pause') || canPublishPath,
  };
}

/** Match stored capabilities to a known MC preset (exact set). */
export function matchSupportSessionPreset(
  capabilities: readonly AuthzAction[] | null | undefined,
): SupportSessionPresetId | null {
  if (!capabilities || capabilities.length === 0) {
    return null;
  }
  const have = new Set(capabilities);
  for (const id of SUPPORT_SESSION_PRESET_IDS) {
    const want = SUPPORT_SESSION_PRESETS[id].capabilities;
    if (want.length !== have.size) {
      continue;
    }
    if (want.every((action) => have.has(action))) {
      return id;
    }
  }
  return null;
}

export interface AuthzActor {
  userId: string;
  /** Null when the user has no membership and no legacy owner fallback applies. */
  projectRole: ProjectMemberRole | null;
  passKind: StudioPassKind | null;
  /** Platform hat (null for tenant-only actors). */
  platformRole?: PlatformRole | null;
  /**
   * Active operator support grant capabilities for this project.
   * When set, `can()` allows listed actions without project membership.
   */
  supportCapabilities?: readonly AuthzAction[] | null;
}

const MEMBER_ACTIONS: ReadonlySet<AuthzAction> = new Set([
  'project.read',
  'draft.edit',
  'project.reconfigure',
]);

const OWNER_PUBLISH_ACTIONS: ReadonlySet<AuthzAction> = new Set([
  'publish.staging',
  'publish.production',
  'publish.domains',
  'project.delete',
  'project.pause',
]);

export function isProjectMemberRole(value: string): value is ProjectMemberRole {
  return (PROJECT_MEMBER_ROLES as readonly string[]).includes(value);
}

export function isPlatformOperatorRole(value: string): value is PlatformOperatorRole {
  return (PLATFORM_OPERATOR_ROLES as readonly string[]).includes(value);
}

export function isAuthzAction(value: string): value is AuthzAction {
  return (AUTHZ_ACTIONS as readonly string[]).includes(value);
}

/** Clamp support session TTL to configured bounds (minutes). */
export function clampSupportSessionTtlMinutes(requested?: number | null): number {
  if (requested == null || !Number.isFinite(requested)) {
    return SUPPORT_SESSION_DEFAULT_TTL_MINUTES;
  }
  const rounded = Math.round(requested);
  if (rounded < SUPPORT_SESSION_MIN_TTL_MINUTES) {
    return SUPPORT_SESSION_MIN_TTL_MINUTES;
  }
  if (rounded > SUPPORT_SESSION_MAX_TTL_MINUTES) {
    return SUPPORT_SESSION_MAX_TTL_MINUTES;
  }
  return rounded;
}

/**
 * Pure policy check — does not load membership from the database.
 */
export function can(actor: AuthzActor, action: AuthzAction): boolean {
  if (action === 'admin.read') {
    return actor.platformRole === 'operator' || actor.platformRole === 'superadmin';
  }

  if (action === 'operators.manage') {
    return actor.platformRole === 'superadmin';
  }

  if (actor.supportCapabilities?.includes(action)) {
    return true;
  }

  if (!actor.projectRole) {
    return false;
  }

  if (MEMBER_ACTIONS.has(action)) {
    return actor.projectRole === 'owner' || actor.projectRole === 'editor';
  }

  if (OWNER_PUBLISH_ACTIONS.has(action)) {
    return actor.projectRole === 'owner' && actor.passKind === 'claimed';
  }

  return false;
}

/** Superadmin always; operators only when `canStartSupport` was granted. */
export function canStartSupportSession(input: {
  role: PlatformOperatorRole;
  canStartSupport: boolean;
}): boolean {
  if (input.role === 'superadmin') {
    return true;
  }
  return input.role === 'operator' && input.canStartSupport === true;
}
